import React, { useEffect, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { CloseThin } from '@/resources/assets/images';
import { sendAnalytics } from '@/services/analyticsService';
import type { RootState } from '@/rootReducer';
import Slider from '@/components/Slider/Slider';
import type { Business } from '../../dashboardTypes';
import './SaveToListModal.scss';
import Button from '@/components/Button/newButtons';

interface Props {
	closeSaveToList: () => void;
	saveListRadioInputValueSelected: string;
	setSaveListRadioInputValueSelected: (x: string) => void;
	saveListSliderValue: number;
	setSaveListSliderValue: (x: number) => void;
	selectListAmount: (x: number) => void;
	saveToListLoading: boolean;
	saveBusinessesToList: (b: Business[]) => void;
	largeListToSave?: Business[];
}

const SaveToListModalRedesign = ({
	closeSaveToList,
	saveListRadioInputValueSelected,
	setSaveListRadioInputValueSelected,
	saveListSliderValue,
	setSaveListSliderValue,
	selectListAmount,
	saveToListLoading,
	saveBusinessesToList,
	largeListToSave
}: Props): JSX.Element => {
	const [user, saveToListBusinesses, availableBusinessesCount] =
		useSelector<RootState>(
			(state: RootState) => [
				state.user.getIn(['authorize', 'user']).toJS(),
				state.dashboard.getIn(['lists', 'selectedBusinesses']),
				state.dashboard.getIn(['businesses', 'count'])
			],
			shallowEqual
		);
	const modalRef = useRef<HTMLDivElement>(null);

	const [buttonIsDisabled, setButtonIsDisabled] = React.useState(
		saveListRadioInputValueSelected === '' && saveListSliderValue === 0
	);

	const buttonIsDisabledRef = useRef(buttonIsDisabled);

	useEffect(() => {
		const isDisabled =
			saveListRadioInputValueSelected === '' && saveListSliderValue === 0;
		setButtonIsDisabled(isDisabled);
		buttonIsDisabledRef.current = isDisabled; // Keep the ref in sync with the latest value
	}, [saveListRadioInputValueSelected, saveListSliderValue]);

	const saveListSliderOnChange = (value: number) => {
		setSaveListSliderValue(value);
	};

	const handleSaveListRadioInputValueChange = e => {
		const numValue = Number(e.target.value);
		setSaveListRadioInputValueSelected(e.target.value);
		setSaveListSliderValue(numValue);
		selectListAmount(numValue);
	};

	const saveToListButtonText = () => {
		if (saveToListLoading) {
			return (
				<div className="save-to-list-button-text">
					Selecting businesses
					<span className="save-to-list-button-ellipsis" />
				</div>
			);
		}

		return `Save Selected to List`;
	};

	const saveBusinessesToListUsingState = () => {
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'Save to list button clicked'
			});
		}

		if (largeListToSave?.length > 0) {
			saveBusinessesToList(largeListToSave);
		}
	};

	const deselectAllButtonClickHandler = () => {
		if (!saveToListLoading && !buttonIsDisabledRef.current) {
			setSaveListRadioInputValueSelected('');
			selectListAmount(0);
			setSaveListSliderValue(0);

			// Remove the "activated" class from the header
			const activatedElement = document.querySelector(
				'.search-businesses-list-header-column-0.activated'
			);
			if (activatedElement) {
				activatedElement.classList.remove('activated');
			}
		}
	};

	const saveToListButtonClickHandler = () => {
		if (!saveToListLoading && !buttonIsDisabled) {
			saveBusinessesToListUsingState();
		}
	};

	const onChangeCompleteHandler = () => {
		selectListAmount(saveListSliderValue);
	};
	// Create a ref to hold the latest selected businesses
	const selectedBusinessesRef = useRef(saveToListBusinesses);

	// Update the ref whenever saveToListBusinesses changes
	useEffect(() => {
		selectedBusinessesRef.current = saveToListBusinesses;
	}, [saveToListBusinesses]);

	useEffect(() => {
		setSaveListSliderValue(saveToListBusinesses?.length);

		if (
			Number(saveListRadioInputValueSelected) !==
			saveToListBusinesses?.length
		) {
			setSaveListRadioInputValueSelected('');
		}

		if (saveToListBusinesses?.length === 50) {
			setSaveListRadioInputValueSelected('50');
		}

		if (saveToListBusinesses?.length === 100) {
			setSaveListRadioInputValueSelected('100');
		}

		if (saveToListBusinesses?.length === 250) {
			setSaveListRadioInputValueSelected('250');
		}

		if (saveToListBusinesses?.length === 400) {
			setSaveListRadioInputValueSelected('400');
		}

		if (saveToListBusinesses?.length === 3000) {
			setSaveListRadioInputValueSelected('3000');
		}
		if (saveToListBusinesses?.length > availableBusinessesCount) {
			setSaveListRadioInputValueSelected(availableBusinessesCount);
			setSaveListSliderValue(availableBusinessesCount);
		}
	}, [saveToListBusinesses?.length]);

	return (
		<div
			className="save-to-list-container"
			ref={modalRef}
			onClick={e => e.stopPropagation()}
		>
			<span>
				Pick businesses to save — you can add them to a sequence next.
			</span>
			<CloseThin
				onClick={() => {
					closeSaveToList();
				}}
				className="save-to-list-close-icon"
			/>
			<div className="save-to-list-selection-container">
				<div className="save-to-list-selection-input-container">
					<input
						className="save-to-list-selection-input"
						type="radio"
						id="save-to-list-selection-input1"
						value="50"
						checked={saveListRadioInputValueSelected === '50'}
						onChange={handleSaveListRadioInputValueChange}
					/>
					<label
						className="save-to-list-selection-label"
						htmlFor="save-to-list-selection-input1"
					>
						50
					</label>
				</div>
				<div className="save-to-list-selection-input-container">
					<input
						className="save-to-list-selection-input"
						type="radio"
						id="save-to-list-selection-input2"
						value="100"
						checked={saveListRadioInputValueSelected === '100'}
						onChange={handleSaveListRadioInputValueChange}
					/>
					<label
						className="save-to-list-selection-label"
						htmlFor="save-to-list-selection-input2"
					>
						100
					</label>
				</div>
				<div className="save-to-list-selection-input-container">
					<input
						className="save-to-list-selection-input"
						type="radio"
						id="save-to-list-selection-input3"
						value="200"
						checked={saveListRadioInputValueSelected === '200'}
						onChange={handleSaveListRadioInputValueChange}
					/>
					<label
						className="save-to-list-selection-label"
						htmlFor="save-to-list-selection-input3"
					>
						200
					</label>
				</div>
				<div className="save-to-list-selection-input-container">
					<input
						className="save-to-list-selection-input"
						type="radio"
						id="save-to-list-selection-input3"
						value="400"
						checked={saveListRadioInputValueSelected === '400'}
						onChange={handleSaveListRadioInputValueChange}
					/>
					<label
						className="save-to-list-selection-label"
						htmlFor="save-to-list-selection-input3"
					>
						400
					</label>
				</div>
				<div className="save-to-list-selection-input-container">
					<input
						className="save-to-list-selection-input"
						type="radio"
						id="save-to-list-selection-input4"
						value="3000"
						checked={saveListRadioInputValueSelected === '3000'}
						onChange={handleSaveListRadioInputValueChange}
					/>
					<label
						className="save-to-list-selection-label"
						htmlFor="save-to-list-selection-input4"
					>
						3000
					</label>
				</div>
			</div>
			<Slider
				value={saveListSliderValue}
				onChange={saveListSliderOnChange}
				onChangeCompleteHandler={onChangeCompleteHandler}
			/>
			<div className="flex justify-between pt-8">
				<Button
					variant="text"
					disabled={saveToListLoading || buttonIsDisabled}
					onClick={deselectAllButtonClickHandler}
					className="whitespace-nowrap"
				>
					Deselect All
				</Button>
				<Button
					variant="primary"
					onClick={saveToListButtonClickHandler}
					disabled={saveToListLoading || buttonIsDisabled}
					className="whitespace-nowrap"
				>
					{saveToListButtonText()}
				</Button>
			</div>
		</div>
	);
};

export default SaveToListModalRedesign;
